import {
  Group,
  GroupUser,
  useCreateGroup,
  useDeleteGroup,
  useGroupUsers,
  useUpdateGroup,
} from "@/api/groups";
import {
  useCheckBulkPermissions,
  useCreateRelationship,
  useDeleteRelationship,
} from "@/api/relations";
import { useOrgUsers } from "@/api/users";
import {
  BackRow,
  chipButtonClassName,
  DirtyField,
  GroupLabel,
  GroupTile,
  NESTED_DIALOG_Z,
  outlineDestructiveClassName,
  paneInputClassName,
  Row,
  smButtonClassName,
} from "@/components/AdminPanel/Organization/components/manage-pane-primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { OrganizationUser, Relation } from "@cohesive/api-client";
import { PersonAvatar } from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, UserRoundPlus, X } from "lucide-react";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";

function DeleteGroupConfirmDialog({
  group,
  open,
  onOpenChange,
  onDeleted,
}: {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  const { mutateAsync: deleteGroup, isPending } = useDeleteGroup(group.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn("sm:max-w-[425px]", NESTED_DIALOG_Z)}
        overlayClassName={NESTED_DIALOG_Z}
      >
        <DialogHeader>
          <DialogTitle>Delete group</DialogTitle>
          <DialogDescription>
            Delete the group &ldquo;{group.name}&rdquo;? This action cannot be
            undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            className="bg-spectrum-red text-paper hover:bg-[color-mix(in_oklab,var(--spectrum-red)_88%,var(--ink))]"
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              try {
                await deleteGroup();
                analytics.trackGroupDeleted({ group });
                onOpenChange(false);
                onDeleted();
                toast.success("Group deleted.");
              } catch {
                toast.error("Failed to delete group. Please try again.");
              }
            }}
          >
            Delete group
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupDetailPane({
  group,
  access,
  onBack,
  onDeleted,
}: {
  group: Group;
  access: GroupAccess;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: groupUsers = [] } = useGroupUsers(group.id, {
    canViewMembers: access.viewMembers,
  });
  const { data: orgUsers = [] } = useOrgUsers();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateGroup(
    group.id,
  );
  const createRelationship = useCreateRelationship();
  const removeRelationship = useDeleteRelationship();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const outside = useMemo(() => {
    const inGroup = new Set(groupUsers.map((u) => u.id));
    return orgUsers.filter((u) => !inGroup.has(u.id));
  }, [orgUsers, groupUsers]);

  const saveField = async (patch: { name?: string; description?: string }) => {
    try {
      await updateGroup({
        name: patch.name ?? group.name,
        description: patch.description ?? group.description,
      });
      analytics.trackGroupUpdated({ group });
      toast.success("Group updated.");
    } catch {
      toast.error("Failed to update group. Please try again.");
      throw new Error("update failed");
    }
  };

  const handleRemoveUser = async (user: GroupUser) => {
    if (!user.relation) {
      toast.error("Could not find user relation.");
      return;
    }
    if (user.relation === "owner") {
      toast.error("Group owners cannot be removed from the group.");
      return;
    }
    try {
      await removeRelationship.mutateAsync({
        resource_object_type: "group",
        resource_object_id: group.id,
        relation: user.relation as Relation,
        subject_object_type: "organization_user",
        subject_object_id: user.id,
      });
      analytics.trackGroupMemberRemoved({ group, member: user });
      await queryClient.invalidateQueries({
        queryKey: ["groupUsers", group.id],
      });
    } catch {
      toast.error("Failed to remove member. Please try again.");
    }
  };

  const handleAddUser = async (user: OrganizationUser) => {
    try {
      await createRelationship.mutateAsync({
        resource_object_type: "group",
        resource_object_id: group.id,
        relation: "member",
        subject_object_type: "organization_user",
        subject_object_id: user.id,
      });
      analytics.trackGroupMemberAdded({ group, member: user });
      await queryClient.invalidateQueries({
        queryKey: ["groupUsers", group.id],
      });
      toast.success("Member added.");
    } catch {
      toast.error("Failed to add member. Please try again.");
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <BackRow label="All groups" onBack={onBack} />
      <div className="flex items-center gap-3">
        <GroupTile name={group.name} className="size-12 text-md" />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-medium text-n-90">
            {group.name}
          </h3>
          <p className="text-sm text-n-50">
            {groupUsers.length} {groupUsers.length === 1 ? "member" : "members"}
          </p>
        </div>
      </div>
      <p className="text-sm text-n-50">
        Share a workspace or skill with the group and every member gets it.
      </p>
      {access.edit ? (
        <div className="flex flex-col gap-4">
          <DirtyField
            label="Group name"
            initial={group.name}
            isSaving={isUpdating}
            onSave={(name) => saveField({ name })}
          />
          <DirtyField
            label="Description"
            initial={group.description ?? ""}
            isSaving={isUpdating}
            onSave={(description) => saveField({ description })}
          />
        </div>
      ) : group.description?.trim() ? (
        <p className="max-w-measure px-2.5 text-sm text-n-50">
          {group.description}
        </p>
      ) : null}
      <section className="flex flex-col gap-0.5">
        <GroupLabel>Members</GroupLabel>
        {groupUsers.map((user) => {
          const displayName = user.name || user.email;
          const isOwner = user.relation === "owner";
          return (
            <Row
              key={user.id}
              frameless
              tile={
                <PersonAvatar
                  name={displayName}
                  email={user.email}
                  pictureUrl={user.picture_url}
                />
              }
              name={displayName}
              meta={isOwner ? `${user.email} · Owner` : user.email}
            >
              {isOwner ? (
                <span className="shrink-0 pr-2 text-sm text-n-50">Owner</span>
              ) : (
                access.manageMembers && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${displayName} from ${group.name}`}
                    className="shrink-0 text-n-50 hover:text-n-90"
                    onClick={() => void handleRemoveUser(user)}
                  >
                    <X />
                  </Button>
                )
              )}
            </Row>
          );
        })}
        {access.manageMembers && outside.length > 0 ? (
          // `px-2.5` is the Row's own inset — without it this button hung a
          // row's width of padding left of the member list it belongs to.
          <div className="px-2.5 pt-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  className={smButtonClassName}
                >
                  <UserRoundPlus />
                  Add member
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {outside.map((user) => (
                  // Stacked, not side by side: the email was pinned
                  // `shrink-0`, so a long one pushed straight out of the menu
                  // instead of giving way. And an account with no name showed
                  // its email twice — once as the name, once beside it — so
                  // the second line only appears when it says something new.
                  <DropdownMenuItem
                    key={user.id}
                    className="flex-col items-start gap-0"
                    onSelect={() => {
                      void handleAddUser(user);
                    }}
                  >
                    <span className="w-full truncate">
                      {user.name || user.email}
                    </span>
                    {user.name ? (
                      <span className="w-full truncate text-xs text-n-40">
                        {user.email}
                      </span>
                    ) : null}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </section>
      {access.remove && (
        <div className="flex items-center">
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className={cn(smButtonClassName, outlineDestructiveClassName)}
            onClick={() => setConfirmDelete(true)}
          >
            Delete group
          </Button>
        </div>
      )}

      <DeleteGroupConfirmDialog
        group={group}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        onDeleted={onDeleted}
      />
    </div>
  );
}

function GroupListRow({
  group,
  access,
  onOpen,
  onDeleted,
}: {
  group: Group;
  access: GroupAccess;
  onOpen: () => void;
  onDeleted: () => void;
}) {
  const { data: groupUsers = [] } = useGroupUsers(group.id, {
    canViewMembers: access.viewMembers,
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const memberCount = groupUsers.length;
  // No count when the membership is unreadable — "0 members" would be a
  // guess, and the row must not claim something it was refused.
  const metaParts = [
    access.viewMembers
      ? `${memberCount} ${memberCount === 1 ? "member" : "members"}`
      : "Members hidden",
    group.description?.trim() || null,
  ].filter(Boolean);

  return (
    <>
      <Row
        frameless
        tile={<GroupTile name={group.name} className="size-9 text-sm" />}
        name={group.name}
        meta={metaParts.join(" · ")}
        onOpen={access.viewMembers ? onOpen : undefined}
      >
        {access.viewMembers && (
          <Button
            variant="ghost"
            size="sm"
            className={cn("shrink-0", chipButtonClassName)}
            onClick={onOpen}
          >
            Manage
          </Button>
        )}
        {/* The menu holds only verbs this user can actually complete; with
            none of them it doesn't render, rather than offering a trigger
            that opens an empty sheet. */}
        {(access.edit || access.remove) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`More actions for ${group.name}`}
                className="shrink-0 text-n-50 hover:text-n-90"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {access.edit && (
                <DropdownMenuItem onSelect={onOpen}>Rename…</DropdownMenuItem>
              )}
              {access.remove && (
                <DropdownMenuItem
                  className="text-spectrum-red focus:text-spectrum-red data-[highlighted]:text-spectrum-red"
                  onSelect={() => {
                    window.setTimeout(() => setConfirmDelete(true), 0);
                  }}
                >
                  Delete group
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </Row>
      <DeleteGroupConfirmDialog
        group={group}
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        onDeleted={onDeleted}
      />
    </>
  );
}

/**
 * What the server will actually allow on a group, per group.
 *
 * Being an org admin buys nothing here: every group verb is gated in Authzed
 * on the group itself — `can_edit` and `can_manage_assignments` are
 * owner+manager, `can_delete` is owner, and even reading the member list needs
 * `can_view_assignments` (owner+manager+member). The groups LIST, by contrast,
 * is only org-scoped, so an admin sees every group in the org and then gets a
 * 403 for each one they aren't personally on.
 *
 * One bulk call answers all four questions for every listed group, so the UI
 * can offer exactly the verbs that will succeed instead of discovering the
 * refusal after the click.
 */
const GROUP_PERMISSIONS = [
  "can_view_assignments",
  "can_edit",
  "can_manage_assignments",
  "can_delete",
] as const;

export type GroupAccess = {
  viewMembers: boolean;
  edit: boolean;
  manageMembers: boolean;
  remove: boolean;
};

const NO_ACCESS: GroupAccess = {
  viewMembers: false,
  edit: false,
  manageMembers: false,
  remove: false,
};

function useGroupAccess(groups: Group[]): Record<string, GroupAccess> {
  const checks = useMemo(
    () =>
      groups.flatMap((group) =>
        GROUP_PERMISSIONS.map((permission) => ({
          resource_object_type: "group" as const,
          resource_object_id: group.id,
          permission,
        })),
      ),
    [groups],
  );

  const { data } = useCheckBulkPermissions(
    { checks },
    { enabled: checks.length > 0 },
  );

  return useMemo(() => {
    const out: Record<string, GroupAccess> = {};
    for (const group of groups) {
      const p = data?.group?.[group.id];
      out[group.id] = {
        viewMembers: !!p?.can_view_assignments,
        edit: !!p?.can_edit,
        manageMembers: !!p?.can_manage_assignments,
        remove: !!p?.can_delete,
      };
    }
    return out;
  }, [groups, data]);
}

export function GroupsPane({ groups }: { groups: Group[] }) {
  const access = useGroupAccess(groups);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const createGroup = useCreateGroup();

  const selected = groups.find((g) => g.id === selectedId) ?? null;
  const trimmed = name.trim();
  const valid =
    trimmed.length > 0 &&
    !groups.some((g) => g.name.toLowerCase() === trimmed.toLowerCase());

  const create = async () => {
    if (!valid || createGroup.isPending) return;
    try {
      const group = await createGroup.mutateAsync({
        name: trimmed,
        description: "",
      });
      analytics.trackGroupCreated({ group });
      setName("");
      setSelectedId(group.id);
      toast.success("Group created.");
    } catch {
      toast.error("Failed to create group. Please try again.");
    }
  };

  if (selected) {
    return (
      <GroupDetailPane
        group={selected}
        access={access[selected.id] ?? NO_ACCESS}
        onBack={() => setSelectedId(null)}
        onDeleted={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex items-center gap-2 px-2.5">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void create();
            }
          }}
          placeholder="New group name"
          className={cn(paneInputClassName, "max-w-72")}
          aria-label="New group name"
        />
        <Button
          variant="secondary"
          size="sm"
          className={smButtonClassName}
          onClick={() => void create()}
          disabled={!valid}
          isLoading={createGroup.isPending}
        >
          Create
        </Button>
        <div className="flex-1" />
        <span className="shrink-0 font-mono text-2xs text-n-40">
          {groups.length} {groups.length === 1 ? "group" : "groups"}
        </span>
      </div>
      {groups.length === 0 ? (
        <p className="max-w-measure px-2.5 text-sm text-n-50">
          No groups yet. A group shares workspaces and skills with a set of
          teammates in one move.
        </p>
      ) : (
        <section className="flex flex-col gap-0.5">
          <GroupLabel>Groups</GroupLabel>
          {groups.map((group) => (
            <GroupListRow
              key={group.id}
              group={group}
              access={access[group.id] ?? NO_ACCESS}
              onOpen={() => {
                analytics.trackGroupOpened({ group });
                setSelectedId(group.id);
              }}
              onDeleted={() => {
                if (selectedId === group.id) setSelectedId(null);
              }}
            />
          ))}
        </section>
      )}
    </div>
  );
}
